import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Briefcase,
  Plus,
  Search,
  Clock,
  CheckCircle2,
  AlertCircle,
  Calendar,
  X,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  DollarSign,
  User,
  Sparkles,
  RefreshCw,
  Info,
  Wallet,
  Users,
  ArrowRightLeft,
  Percent,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Modal from "../../components/common/Modal";
import { PaginationFooter } from "../../components/common/PaginationFooter";
import { useAuthState } from "react-redux-django-auth";
import {
  fetchJobs,
  fetchJobsPaginated,
  fetchAgents,
  fetchServiceRates,
  createJob,
  updateJob,
  deleteJob,
  handoffDeliverable,
  Job,
  Agent,
  ServiceRate,
  ServiceType,
  JobStatus,
  StaffJobAnalytics,
} from "../../services/jobService";
import { authClientWeb } from "react-redux-django-auth/web";

const GlassCard = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}
  >
    {children}
  </div>
);

const SERVICE_TYPES: { label: string; value: ServiceType }[] = [
  { label: "NERD (₦500 base commission)", value: "NERD" },
  { label: "Affidavit (₦500 base commission)", value: "AFFIDAVIT" },
  { label: "CAC Registration", value: "CAC" },
  { label: "TIN Registration", value: "TIN" },
  { label: "Web Design", value: "WEB_DESIGN" },
  { label: "Other Service", value: "OTHER" },
];

type AdminMetricInfoKey = "TOTAL_JOBS" | "PENDING_JOBS" | "PAID_COMMISSIONS" | "PENDING_PAYOUT" | "AWAITING_APPROVAL";

const AdminStaffJobs: React.FC = () => {
  const { user } = useAuthState();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  const [serverAnalytics, setServerAnalytics] = useState<StaffJobAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMetricInfo, setSelectedMetricInfo] = useState<AdminMetricInfoKey | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10) || 1;
  const pageSize = parseInt(searchParams.get("page_size") || "20", 10) || 20;
  const [totalCount, setTotalCount] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
  const loadRequestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (currentPage !== 1) {
        updateQueryParams({ page: 1 });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setDebouncedSearch(searchQuery);
      if (currentPage !== 1) {
        updateQueryParams({ page: 1 });
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setDebouncedSearch("");
    if (currentPage !== 1) {
      updateQueryParams({ page: 1 });
    }
  };

  const updateQueryParams = (newParams: { page?: number; page_size?: number }) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newParams.page !== undefined) next.set("page", String(newParams.page));
      if (newParams.page_size !== undefined) next.set("page_size", String(newParams.page_size));
      return next;
    }, { replace: true });
  };

  const handlePageChange = (newPage: number) => {
    updateQueryParams({ page: newPage });
  };

  const handlePageSizeChange = (newSize: number) => {
    updateQueryParams({ page: 1, page_size: newSize });
  };

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [monthFilter, setMonthFilter] = useState("ALL");
  const [referralFilter, setReferralFilter] = useState("ALL");

  const dynamicServiceTypes = useMemo(() => {
    if (serviceRates.length > 0) {
      return serviceRates
        .filter((sr) => sr.is_active)
        .map((sr) => {
          const comm = parseFloat(sr.base_commission || "0");
          const commLabel = comm > 0 ? ` (₦${comm.toLocaleString("en-NG")} base commission)` : "";
          return {
            label: `${sr.name}${commLabel}`,
            value: sr.code,
          };
        });
    }
    return SERVICE_TYPES;
  }, [serviceRates]);

  // Modal & Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const todayStr = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    date: todayStr,
    customer_name: "",
    job_type: "NERD" as ServiceType,
    referral_source: "DIRECT" as "WALK_IN" | "STAFF" | "DIRECT",
    agent_profile: "" as string,
    amount_paid: "0",
    status: "PENDING" as JobStatus,
    remark: "",
  });

  // Handoff & Commission Split State
  const [isHandoffModalOpen, setIsHandoffModalOpen] = useState(false);
  const [handoffTargetJob, setHandoffTargetJob] = useState<Job | null>(null);
  const [availableSpecialists, setAvailableSpecialists] = useState<{ id: number; name: string; email: string }[]>([]);
  const [handoffSuccessorId, setHandoffSuccessorId] = useState<string>("");
  const [handoffPredecessorSplit, setHandoffPredecessorSplit] = useState<number>(50);
  const [handoffSuccessorSplit, setHandoffSuccessorSplit] = useState<number>(50);
  const [handoffReason, setHandoffReason] = useState<string>("");
  const [handoffSubmitting, setHandoffSubmitting] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [handoffSuccess, setHandoffSuccess] = useState<string | null>(null);

  const loadData = async (options?: {
    month?: string;
    page?: number;
    pageSize?: number;
    search?: string;
    status?: string;
    job_type?: string;
    referral_source?: string;
  }) => {
    const currentReqId = ++loadRequestIdRef.current;
    try {
      setTableLoading(true);
      const activeMonth = options?.month !== undefined ? options.month : monthFilter;
      const activePage = options?.page !== undefined ? options.page : currentPage;
      const activePageSize = options?.pageSize !== undefined ? options.pageSize : pageSize;
      const activeSearch = options?.search !== undefined ? options.search : debouncedSearch;
      const activeStatus = options?.status !== undefined ? options.status : statusFilter;
      const activeType = options?.job_type !== undefined ? options.job_type : typeFilter;
      const activeReferral = options?.referral_source !== undefined ? options.referral_source : referralFilter;

      const jobsRes = await fetchJobsPaginated({
        month: activeMonth,
        page: activePage,
        pageSize: activePageSize,
        scope: "personal",
        search: activeSearch,
        status: activeStatus,
        job_type: activeType,
        referral_source: activeReferral,
      });

      if (currentReqId !== loadRequestIdRef.current) {
        return;
      }

      const safeJobs = Array.isArray(jobsRes.data) ? jobsRes.data : [];
      setJobs(safeJobs);
      setTotalCount(jobsRes.count || 0);
      if (jobsRes.analytics) {
        setServerAnalytics(jobsRes.analytics);
      }
    } catch (err: any) {
      if (currentReqId !== loadRequestIdRef.current) return;
      console.error("Failed to load jobs:", err);
      setJobs([]);
      setTotalCount(0);
    } finally {
      if (currentReqId === loadRequestIdRef.current) {
        setLoading(false);
        setTableLoading(false);
        setRefreshing(false);
      }
    }
  };

  // Load static reference options once on mount
  useEffect(() => {
    fetchAgents().then((data) => { if (Array.isArray(data)) setAgents(data); }).catch(() => {});
    fetchServiceRates().then((data) => { if (Array.isArray(data)) setServiceRates(data); }).catch(() => {});
  }, []);

  useEffect(() => {
    loadData();
  }, [
    currentPage,
    pageSize,
    debouncedSearch,
    monthFilter,
    statusFilter,
    typeFilter,
    referralFilter,
  ]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAgents().then((data) => { if (Array.isArray(data)) setAgents(data); }).catch(() => {});
    fetchServiceRates().then((data) => { if (Array.isArray(data)) setServiceRates(data); }).catch(() => {});
    loadData();
  };

  // Open modal for new job
  const handleOpenNewModal = () => {
    setEditingJob(null);
    setFormError(null);
    setFormData({
      date: todayStr,
      customer_name: "",
      job_type: "NERD",
      referral_source: "DIRECT",
      agent_profile: "",
      amount_paid: "0",
      status: "PENDING",
      remark: "",
    });
    setIsModalOpen(true);
  };

  // Open modal for editing job
  const handleOpenEditModal = (job: Job) => {
    if (job.is_approved) {
      alert(
        "This job has already been approved by SuperAdmin and cannot be edited.",
      );
      return;
    }
    setEditingJob(job);
    setFormError(null);

    let refSource: "WALK_IN" | "STAFF" | "DIRECT" = "DIRECT";
    if (job.referral_source === "DIRECT") {
      refSource = "DIRECT";
    } else if (job.referral_source === "STAFF" || job.is_staff_referral) {
      refSource = "STAFF";
    } else if (job.referral_source === "WALK_IN") {
      refSource = "WALK_IN";
    } else if (parseFloat(job.amount_paid || "0") === 0) {
      refSource = "DIRECT";
    } else {
      refSource = "WALK_IN";
    }

    setFormData({
      date: job.date || todayStr,
      customer_name: job.customer_name || "",
      job_type: job.job_type || "NERD",
      referral_source: refSource,
      agent_profile: job.agent_profile ? String(job.agent_profile) : "",
      amount_paid:
        refSource === "DIRECT"
          ? "0"
          : job.amount_paid
            ? String(job.amount_paid)
            : "",
      status: job.status || "PENDING",
      remark: job.remark || "",
    });
    setIsModalOpen(true);
  };

  // Live Commission Preview
  const previewCommission = useMemo(() => {
    const paid = parseFloat(formData.amount_paid) || 0;
    let comm = 0;

    const currentRate = serviceRates.find((sr) => sr.code === formData.job_type);
    if (currentRate) {
      comm += parseFloat(currentRate.base_commission || "0");
    } else if (formData.job_type === "NERD" || formData.job_type === "AFFIDAVIT") {
      comm += 500;
    }

    if (formData.referral_source === "STAFF" && formData.job_type === "NERD") {
      if (paid < 11000) {
        return {
          valid: false,
          error: "Staff referrals on NERD require Amount Paid ≥ ₦11,000.",
          amount: 0,
        };
      }
      comm += 500; // Extra ₦500 referral fee
      if (paid > 11000) {
        comm += paid - 11000; // Staff takes all excess
      }
    }

    return { valid: true, error: null, amount: comm };
  }, [formData.job_type, formData.referral_source, formData.amount_paid, serviceRates]);

  // Form Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.customer_name.trim()) {
      setFormError("Customer name is required.");
      return;
    }

    const paidNum = parseFloat(formData.amount_paid) || 0;
    if (
      formData.referral_source === "STAFF" &&
      formData.job_type === "NERD" &&
      paidNum < 11000
    ) {
      setFormError(
        "NERD Staff Referrals must have Amount Paid of at least ₦11,000.",
      );
      return;
    }

    if (formData.referral_source !== "DIRECT" && !formData.amount_paid.trim()) {
      setFormError("Amount Paid is required.");
      return;
    }

    setFormSubmitting(true);
    try {
      const currentUserId = (user as any)?.id || (user as any)?.user?.id;
      const payload: any = {
        date: formData.date,
        customer_name: formData.customer_name.trim(),
        job_type: formData.job_type,
        referral_source: formData.referral_source,
        is_staff_referral: formData.referral_source === "STAFF",
        agent_profile: editingJob ? editingJob.agent_profile || null : null,
        assigned_staff: currentUserId || undefined,
        scope: "personal",
        amount_paid:
          formData.referral_source === "DIRECT"
            ? (editingJob && parseFloat(editingJob.amount_paid || "0") > 0 ? String(editingJob.amount_paid) : "0.00")
            : formData.amount_paid
              ? String(formData.amount_paid)
              : "0.00",
        status: formData.status,
        remark: formData.remark.trim() || undefined,
      };

      if (editingJob) {
        await updateJob(editingJob.id, payload);
      } else {
        await createJob(payload);
      }

      setIsModalOpen(false);
      await loadData();
    } catch (err: any) {
      console.error("Failed to save job:", err);
      const resErr = err.response?.data;
      if (typeof resErr === "object") {
        const firstKey = Object.keys(resErr)[0];
        const msg = Array.isArray(resErr[firstKey])
          ? resErr[firstKey][0]
          : String(resErr[firstKey]);
        setFormError(`${firstKey}: ${msg}`);
      } else {
        setFormError("An unexpected error occurred while saving the job.");
      }
    } finally {
      setFormSubmitting(false);
    }
  };

  // Delete Prompt Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState<boolean>(false);
  const [promptMessage, setPromptMessage] = useState<{ title: string; message: string; type: "error" | "info" } | null>(null);

  const handlePromptDelete = (job: Job) => {
    if (job.is_approved || job.is_commission_paid) {
      setPromptMessage({
        title: "Deletion Restricted",
        message: "This job record has an approved or disbursed commission and cannot be deleted.",
        type: "error",
      });
      return;
    }
    setJobToDelete(job);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!jobToDelete) return;
    try {
      setDeleteSubmitting(true);
      await deleteJob(jobToDelete.id, "personal");
      setDeleteModalOpen(false);
      setJobToDelete(null);
      await loadData();
    } catch (err) {
      console.error("Failed to delete job:", err);
      setPromptMessage({
        title: "Delete Failed",
        message: "Failed to delete job record. Please check your network and permissions.",
        type: "error",
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };

  // ── Multi-Staff Task Handoff & Commission Split Handlers ──────────────────
  const handleOpenHandoffModal = async (job: Job) => {
    setHandoffTargetJob(job);
    setHandoffError(null);
    setHandoffSuccess(null);
    setHandoffSuccessorId("");
    setHandoffPredecessorSplit(50);
    setHandoffSuccessorSplit(50);
    setHandoffReason("");
    setIsHandoffModalOpen(true);

    try {
      const client = authClientWeb();
      if (client) {
        const res = await client.get(`/jobs/${job.id}/handoff/`);
        if (res.data?.staff_list && Array.isArray(res.data.staff_list)) {
          const currentUserId = (user as any)?.id || (user as any)?.user?.id;
          const filtered = res.data.staff_list.filter((s: any) => String(s.id) !== String(currentUserId));
          setAvailableSpecialists(filtered);
          if (filtered.length > 0) {
            setHandoffSuccessorId(String(filtered[0].id));
          }
        }
      }
    } catch (err) {
      console.error("Failed to load staff list for handoff:", err);
    }
  };

  const handleSplitPreset = (predSplit: number) => {
    setHandoffPredecessorSplit(predSplit);
    setHandoffSuccessorSplit(100 - predSplit);
  };

  const handleSubmitHandoff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!handoffTargetJob) return;
    if (!handoffSuccessorId) {
      setHandoffError("Please select a successor specialist to hand off to.");
      return;
    }
    if (handoffPredecessorSplit + handoffSuccessorSplit !== 100) {
      setHandoffError("Commission split percentages must sum up to exactly 100%.");
      return;
    }

    try {
      setHandoffSubmitting(true);
      setHandoffError(null);
      const res = await handoffDeliverable(
        handoffTargetJob.id,
        {
          successor_staff_id: parseInt(handoffSuccessorId, 10),
          predecessor_split_percent: handoffPredecessorSplit,
          successor_split_percent: handoffSuccessorSplit,
          handoff_reason: handoffReason,
        },
        "personal"
      );

      setHandoffSuccess(res.message || "Handoff executed successfully.");
      setTimeout(() => {
        setIsHandoffModalOpen(false);
        loadData();
      }, 1200);
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Failed to execute deliverable handoff. Please try again.";
      setHandoffError(detail);
    } finally {
      setHandoffSubmitting(false);
    }
  };

  // KPIs
  // Available Months for Filtering (Dynamically extracted from logged jobs and server analytics)
  const availableMonths = useMemo(() => {
    const set = new Set<string>();
    const currentMonth = new Date().toISOString().slice(0, 7);
    set.add(currentMonth);
    if ((serverAnalytics as any)?.available_months && Array.isArray((serverAnalytics as any).available_months)) {
      (serverAnalytics as any).available_months.forEach((m: string) => set.add(m));
    }
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    safeJobs.forEach((j) => {
      if (j?.date) {
        set.add(j.date.slice(0, 7));
      }
    });
    return Array.from(set).sort().reverse();
  }, [jobs, serverAnalytics]);

  const formatMonth = (m: string) => {
    if (!m || m === "ALL") return "All Months";
    try {
      const [year, month] = m.split("-");
      const d = new Date(parseInt(year), parseInt(month) - 1, 1);
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    } catch {
      return m;
    }
  };

  // KPIs & Statistics (Powered by authoritative backend analytics or calculated client-side fallback)
  const stats = useMemo(() => {
    if (serverAnalytics && (serverAnalytics as any).role === "staff") {
      const a = serverAnalytics as any;
      return {
        totalJobs: Number(a.total_jobs ?? a.totalJobs ?? 0) || 0,
        completed: Number(a.completed_jobs ?? a.completed ?? 0) || 0,
        pending: Number(a.pending_jobs ?? a.pending ?? 0) || 0,
        approvedCommission: Number(a.approved_commission ?? a.approved_commissions ?? a.approvedCommission ?? 0) || 0,
        paidCommission: Number(a.paid_commission ?? a.paid_commissions ?? a.paidCommission ?? 0) || 0,
        pendingMonthlyPayout: Number(a.pending_monthly_payout ?? a.pendingMonthlyPayout ?? 0) || 0,
        awaitingApprovalCommission: Number(a.awaiting_approval_commission ?? a.pending_commissions ?? a.awaitingApprovalCommission ?? 0) || 0,
      };
    }

    const currentUserId = (user as any)?.id || (user as any)?.user?.id;
    const currentUserName = (user as any)?.name || (user as any)?.user?.name || (user as any)?.get_full_name;
    const isSuperuser = Boolean((user as any)?.is_superuser || (user as any)?.role === "SUPERADMIN");

    const safeJobs = Array.isArray(jobs) ? jobs : [];
    const myJobs = isSuperuser && currentUserId
      ? safeJobs.filter((j) => {
          if (!j) return false;
          if (j.assigned_staff && Number(j.assigned_staff) === Number(currentUserId)) return true;
          if (j.assignments?.some((a) => Number(a.staff) === Number(currentUserId))) return true;
          if (j.user_split) return true;
          if (j.assigned_staff_name && currentUserName && j.assigned_staff_name === currentUserName) return true;
          return false;
        })
      : safeJobs;

    const targetJobs =
      monthFilter === "ALL"
        ? myJobs
        : myJobs.filter((j) => j?.date && j.date.startsWith(monthFilter));

    const totalJobs = targetJobs.length;
    const completed = targetJobs.filter((j) => j?.status === "COMPLETED").length;
    const pending = targetJobs.filter(
      (j) => j && (j.status === "PENDING" || j.status === "IN_PROGRESS"),
    ).length;

    let approvedCommission = 0;
    let paidCommission = 0;
    let pendingMonthlyPayout = 0;
    let awaitingApprovalCommission = 0;

    targetJobs.forEach((j) => {
      if (!j) return;
      const comm =
        parseFloat(j.commission || j.expected_commission || "0") || 0;
      if (j.is_approved) {
        approvedCommission += comm;
        if (j.is_commission_paid) {
          paidCommission += comm;
        } else {
          pendingMonthlyPayout += comm;
        }
      } else {
        awaitingApprovalCommission += comm;
      }
    });

    return {
      totalJobs,
      completed,
      pending,
      approvedCommission,
      paidCommission,
      pendingMonthlyPayout,
      awaitingApprovalCommission,
    };
  }, [jobs, monthFilter, serverAnalytics, user]);

  const getStatusBadge = (status: JobStatus) => {
    switch (status) {
      case "COMPLETED":
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-text-[11px] cht-font-black cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200">
            <CheckCircle2 size={12} /> Completed
          </span>
        );
      case "IN_PROGRESS":
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-text-[11px] cht-font-black cht-bg-blue-50 cht-text-blue-700 cht-border cht-border-blue-200">
            <Clock size={12} /> In Progress
          </span>
        );
      case "CANCELLED":
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-text-[11px] cht-font-black cht-bg-rose-50 cht-text-rose-700 cht-border cht-border-rose-200">
            <AlertCircle size={12} /> Cancelled
          </span>
        );
      default:
        return (
          <span className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-3 cht-py-1 cht-rounded-full cht-text-[11px] cht-font-black cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200">
            <Clock size={12} /> Pending
          </span>
        );
    }
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header */}
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2">
            <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">
              My Staff Jobs & Commissions
            </h1>
            <span className="cht-px-2.5 cht-py-0.5 cht-bg-blue-100 cht-text-blue-700 cht-rounded-full cht-text-xs cht-font-black">
              Staff Mode
            </span>
          </div>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm cht-mt-1">
            Log client backlog tasks, track completion, and view your calculated
            worker commissions.
          </p>
        </div>
        <div className="cht-flex cht-items-center cht-gap-3">
          <Button
            variant="outline"
            leftIcon={
              <RefreshCw
                size={16}
                className={refreshing ? "cht-animate-spin" : ""}
              />
            }
            onClick={handleRefresh}
            className="cht-rounded-2xl"
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus size={18} />}
            onClick={handleOpenNewModal}
            className="cht-rounded-2xl cht-shadow-md hover:cht-shadow-lg"
          >
            Log New Job
          </Button>
        </div>
      </header>

      {/* Monthly Commission Cycle Policy Banner */}
      <div className="cht-flex cht-items-center cht-gap-3 cht-p-4 cht-rounded-[24px] cht-bg-[#3e57da]/5 cht-border cht-border-[#3e57da]/15 cht-text-xs cht-text-gray-700">
        <Calendar size={18} className="cht-text-[#3e57da] cht-shrink-0" />
        <div className="cht-flex-1 cht-flex cht-flex-col sm:cht-flex-row sm:cht-items-center sm:cht-justify-between cht-gap-2">
          <div>
            <span className="cht-font-black cht-text-[#3e57da] cht-mr-1.5">Monthly Commission Cycle:</span>
            <span>Commissions on completed jobs accumulate through the calendar month and are officially disbursed during monthly payroll payout.</span>
          </div>
          {monthFilter !== "ALL" && (
            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-3 cht-py-1 cht-bg-white cht-rounded-xl cht-font-bold cht-text-[#3e57da] cht-border cht-border-[#3e57da]/20 cht-shadow-2xs">
              Filtering: {formatMonth(monthFilter)}
            </span>
          )}
        </div>
      </div>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-3 xl:cht-grid-cols-5 cht-gap-4 lg:cht-gap-6">
        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-blue-50 cht-text-blue-600 cht-rounded-2xl cht-shrink-0">
            <Briefcase size={22} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              My Jobs Logged {monthFilter !== "ALL" ? `(${formatMonth(monthFilter)})` : ""}
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-[#1e293b]">
              {stats.totalJobs}
            </h4>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("TOTAL_JOBS");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-blue-600 hover:cht-bg-blue-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about My Jobs Logged"
            aria-label="My Jobs Logged information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl cht-shrink-0">
            <Clock size={22} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-amber-600 cht-uppercase cht-tracking-widest">
              Total Pending Jobs
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-amber-700">
              {stats.pending}
            </h4>
            <p className="cht-text-[10px] cht-text-gray-400 cht-font-bold cht-mt-0.5">
              Active / In Progress
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("PENDING_JOBS");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Pending Jobs"
            aria-label="Pending Jobs information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl cht-shrink-0">
            <ShieldCheck size={22} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-emerald-600 cht-uppercase cht-tracking-widest">
              Paid Out (Disbursed)
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-emerald-700">
              ₦
              {(stats.paidCommission || 0).toLocaleString("en-NG", {
                minimumFractionDigits: 2,
              })}
            </h4>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("PAID_COMMISSIONS");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-emerald-600 hover:cht-bg-emerald-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Paid Out Commissions"
            aria-label="Paid Out Commissions information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-indigo-50 cht-text-indigo-600 cht-rounded-2xl cht-shrink-0">
            <Wallet size={22} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-indigo-600 cht-uppercase cht-tracking-widest">
              Pending Month Payout
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-indigo-700">
              ₦
              {(stats.pendingMonthlyPayout || 0).toLocaleString("en-NG", {
                minimumFractionDigits: 2,
              })}
            </h4>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("PENDING_PAYOUT");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-indigo-600 hover:cht-bg-indigo-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Pending Month Payout"
            aria-label="Pending Month Payout information"
          >
            <Info size={15} />
          </button>
        </GlassCard>

        <GlassCard className="cht-p-5 lg:cht-p-6 cht-rounded-[28px] cht-relative cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-3.5 lg:cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl cht-shrink-0">
            <AlertCircle size={22} />
          </div>
          <div className="cht-pr-6 cht-min-w-0">
            <p className="cht-text-[10px] cht-font-black cht-text-purple-600 cht-uppercase cht-tracking-widest">
              Awaiting Approval
            </p>
            <h4 className="cht-text-2xl cht-font-black cht-text-purple-700">
              ₦
              {(stats.awaitingApprovalCommission || 0).toLocaleString("en-NG", {
                minimumFractionDigits: 2,
              })}
            </h4>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedMetricInfo("AWAITING_APPROVAL");
            }}
            className="cht-absolute cht-top-3.5 cht-right-3.5 cht-p-1.5 cht-text-gray-400 hover:cht-text-purple-600 hover:cht-bg-purple-50 cht-rounded-xl cht-transition-colors"
            title="Learn more about Awaiting Approval"
            aria-label="Awaiting Approval information"
          >
            <Info size={15} />
          </button>
        </GlassCard>
      </div>

      {/* Filter & Search Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-stretch md:cht-items-center cht-gap-3 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-100 cht-shadow-sm">
        <div className="cht-flex-1">
          <Input
            leftIcon={<Search size={18} />}
            placeholder="Search by customer name, SN, remarks..."
            value={searchQuery}
            onChange={(e: any) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            rightElement={
              searchQuery ? (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="cht-text-gray-400 hover:cht-text-gray-600 cht-transition-colors cht-p-1"
                  title="Clear search"
                >
                  <X size={16} />
                </button>
              ) : null
            }
          />
        </div>

        <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-3">
          <div className="cht-w-44">
            <Input
              as="select"
              value={monthFilter}
              onChange={(e: any) => {
                const newM = e.target.value;
                setMonthFilter(newM);
                if (currentPage !== 1) updateQueryParams({ page: 1 });
              }}
              options={[
                { label: "All Months", value: "ALL" },
                ...availableMonths.map((m) => ({
                  label: `${formatMonth(m)}${m === new Date().toISOString().slice(0, 7) ? " (Current)" : ""}`,
                  value: m,
                })),
              ]}
            />
          </div>

          <div className="cht-w-44">
            <Input
              as="select"
              value={referralFilter}
              onChange={(e: any) => {
                setReferralFilter(e.target.value);
                if (currentPage !== 1) updateQueryParams({ page: 1 });
              }}
              options={[
                { label: "All Referrals", value: "ALL" },
                { label: "Direct Channel", value: "DIRECT" },
                { label: "Walk-in", value: "WALK_IN" },
                { label: "Staff Referral", value: "STAFF" },
              ]}
            />
          </div>

          <div className="cht-w-36">
            <Input
              as="select"
              value={statusFilter}
              onChange={(e: any) => {
                setStatusFilter(e.target.value);
                if (currentPage !== 1) updateQueryParams({ page: 1 });
              }}
              options={[
                { label: "All Statuses", value: "ALL" },
                { label: "Pending", value: "PENDING" },
                { label: "In Progress", value: "IN_PROGRESS" },
                { label: "Completed", value: "COMPLETED" },
                { label: "Cancelled", value: "CANCELLED" },
              ]}
            />
          </div>

          <div className="cht-w-44">
            <Input
              as="select"
              value={typeFilter}
              onChange={(e: any) => {
                setTypeFilter(e.target.value);
                if (currentPage !== 1) updateQueryParams({ page: 1 });
              }}
              options={[
                { label: "All Service Types", value: "ALL" },
                ...dynamicServiceTypes.map((st) => ({
                  label: st.label,
                  value: st.value,
                })),
              ]}
            />
          </div>
        </div>
      </div>

      {/* Main Table */}
      <GlassCard className="cht-rounded-[32px] cht-overflow-hidden">
        {loading ? (
          <div className="cht-py-20 cht-flex cht-flex-col cht-items-center cht-justify-center cht-gap-3 cht-text-gray-400">
            <RefreshCw
              size={32}
              className="cht-animate-spin cht-text-[#3e57da]"
            />
            <p className="cht-text-sm cht-font-bold cht-text-gray-500">
              Loading staff jobs...
            </p>
          </div>
        ) : jobs.length === 0 ? (
          <div className="cht-py-20 cht-text-center">
            <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-400">
              <Briefcase size={28} />
            </div>
            <h4 className="cht-font-bold cht-text-gray-700 cht-text-base">
              No jobs found
            </h4>
            <p className="cht-text-gray-400 cht-text-sm cht-mt-1">
              Try adjusting your search terms or filters.
            </p>
          </div>
        ) : (
          <div className="cht-overflow-x-auto cht-relative">
            {tableLoading && (
              <div className="cht-absolute cht-inset-0 cht-bg-white/60 cht-backdrop-blur-[1px] cht-flex cht-items-center cht-justify-center cht-z-10 cht-transition-opacity">
                <RefreshCw size={24} className="cht-animate-spin cht-text-[#3e57da]" />
              </div>
            )}
            <table className="cht-w-full cht-text-left cht-border-collapse">
              <thead>
                <tr className="cht-border-b cht-border-gray-100 cht-bg-gray-50/50 cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-wider">
                  <th className="cht-py-4 cht-px-6">Job # & Date</th>
                  <th className="cht-py-4 cht-px-6">Customer Name</th>
                  <th className="cht-py-4 cht-px-6">Service</th>
                  <th className="cht-py-4 cht-px-6">Referral</th>
                  <th className="cht-py-4 cht-px-6">Status</th>
                  <th className="cht-py-4 cht-px-6">Commission</th>
                  <th className="cht-py-4 cht-px-6">Payment</th>
                  <th className="cht-py-4 cht-px-6 cht-text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="cht-divide-y cht-divide-gray-50">
                {jobs.map((job, idx) => {
                  const commVal = parseFloat(job.commission || job.expected_commission || "0") || 0;
                  const seqNum = totalCount > 0 ? totalCount - ((currentPage - 1) * pageSize + idx) : idx + 1;
                  return (
                    <tr
                      key={job.id}
                      className="hover:cht-bg-gray-50/60 cht-transition-colors"
                    >
                      <td className="cht-py-4 cht-px-6">
                        <div className="cht-font-black cht-text-sm cht-text-gray-900">
                          #{seqNum}
                        </div>
                        <div className="cht-text-xs cht-text-gray-400 cht-font-medium">
                          {job.date || "Today"}
                        </div>
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        <div className="cht-font-bold cht-text-sm cht-text-gray-900">
                          {job.customer_name}
                        </div>
                        {job.remark && (
                          <div
                            className="cht-inline-flex cht-items-center cht-gap-1.5 cht-mt-1.5 cht-px-2.5 cht-py-1 cht-bg-rose-50 cht-border cht-border-rose-200/90 cht-rounded-lg cht-text-rose-700 cht-text-[11px] cht-font-bold cht-max-w-xs cht-truncate"
                            title={job.remark}
                          >
                            <span className="cht-w-1.5 cht-h-1.5 cht-rounded-full cht-bg-rose-500 cht-flex-shrink-0" />
                            <span className="cht-truncate">{job.remark}</span>
                          </div>
                        )}
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        <span className="cht-inline-block cht-px-3 cht-py-1 cht-bg-slate-100 cht-text-slate-800 cht-font-bold cht-text-xs cht-rounded-lg">
                          {job.job_type_name || job.job_type}
                        </span>
                        {job.is_split && (
                          <div className="cht-mt-1.5">
                            <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-100 cht-rounded-md cht-text-[10px] cht-font-black">
                              <Users size={10} />
                              {job.user_split ? `${parseFloat(job.user_split.split_percentage)}% Handoff Cut` : "Split Task"}
                            </span>
                          </div>
                        )}
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        {job.referral_source === "DIRECT" || (!job.referral_source && !job.is_staff_referral && !job.agent_profile_name && parseFloat(job.amount_paid || "0") === 0) ? (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-full cht-text-xs cht-font-bold">
                            Direct {parseFloat(job.amount_paid || "0") === 0 ? "(₦0)" : ""}
                          </span>
                        ) : job.referral_source === "STAFF" || job.is_staff_referral ? (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-bg-purple-50 cht-text-purple-700 cht-rounded-full cht-text-xs cht-font-bold">
                            <Sparkles size={12} /> Staff
                          </span>
                        ) : job.referral_source === "AGENT" || job.agent_profile_name ? (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-bg-cyan-50 cht-text-cyan-700 cht-rounded-full cht-text-xs cht-font-bold">
                            <User size={12} /> {job.agent_profile_name || "Agent"}
                          </span>
                        ) : (
                          <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-bg-slate-100 cht-text-slate-700 cht-rounded-full cht-text-xs cht-font-medium">
                            Walk-in
                          </span>
                        )}
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        {getStatusBadge(job.status)}
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        <div className="cht-font-black cht-text-sm cht-text-[#3e57da]">
                          ₦
                          {(commVal || 0).toLocaleString("en-NG", {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </td>

                      <td className="cht-py-4 cht-px-6">
                        {job.is_commission_paid ? (
                          <span
                            className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-bg-emerald-50 cht-text-emerald-700 cht-border cht-border-emerald-200 cht-rounded-full cht-text-xs cht-font-black"
                            title={
                              job.commission_paid_at
                                ? `Disbursed on ${new Date(job.commission_paid_at).toLocaleDateString()}`
                                : "Disbursed"
                            }
                          >
                            <ShieldCheck size={14} /> Paid Out
                          </span>
                        ) : job.is_approved ? (
                          <span
                            className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-bg-indigo-50 cht-text-indigo-700 cht-border cht-border-indigo-200 cht-rounded-full cht-text-xs cht-font-bold"
                            title="Approved by Superadmin. Queued for monthly payroll payout."
                          >
                            <Clock size={12} /> Approved • Queued
                          </span>
                        ) : (
                          <span
                            className="cht-inline-flex cht-items-center cht-gap-1.5 cht-px-2.5 cht-py-1 cht-bg-amber-50 cht-text-amber-700 cht-border cht-border-amber-200 cht-rounded-full cht-text-xs cht-font-bold"
                            title="Pending superadmin review"
                          >
                            <Clock size={12} /> Pending Review
                          </span>
                        )}
                      </td>

                      <td className="cht-py-4 cht-px-6 cht-text-right">
                        <div className="cht-flex cht-items-center cht-justify-end cht-gap-2">
                          <button
                            onClick={() => handleOpenHandoffModal(job)}
                            disabled={job.is_commission_paid || job.status === "COMPLETED"}
                            className={`cht-p-2 cht-rounded-xl cht-transition-colors ${
                              job.is_commission_paid || job.status === "COMPLETED"
                                ? "cht-text-gray-300 cht-cursor-not-allowed"
                                : "cht-text-gray-500 hover:cht-bg-indigo-50 hover:cht-text-[#3e57da]"
                            }`}
                            title={
                              job.is_commission_paid
                                ? "Paid commissions cannot be handed off"
                                : job.status === "COMPLETED"
                                ? "Completed deliverables cannot be handed off"
                                : "Handoff / Split Deliverable with another specialist"
                            }
                          >
                            <ArrowRightLeft size={16} />
                          </button>

                          <button
                            onClick={() => handleOpenEditModal(job)}
                            disabled={job.is_approved || job.is_commission_paid}
                            className={`cht-p-2 cht-rounded-xl cht-transition-colors ${
                              job.is_approved || job.is_commission_paid
                                ? "cht-text-gray-300 cht-cursor-not-allowed"
                                : "cht-text-gray-500 hover:cht-bg-blue-50 hover:cht-text-[#3e57da]"
                            }`}
                            title={
                              job.is_commission_paid
                                ? "Paid commissions cannot be edited"
                                : job.is_approved
                                ? "Approved jobs cannot be edited"
                                : "Edit job"
                            }
                          >
                            <Edit2 size={16} />
                          </button>

                          <button
                            onClick={() => handlePromptDelete(job)}
                            disabled={job.is_approved || job.is_commission_paid}
                            className={`cht-p-2 cht-rounded-xl cht-transition-colors ${
                              job.is_approved || job.is_commission_paid
                                ? "cht-text-gray-300 cht-cursor-not-allowed"
                                : "cht-text-gray-500 hover:cht-bg-rose-50 hover:cht-text-rose-600"
                            }`}
                            title={
                              job.is_commission_paid
                                ? "Paid commissions cannot be deleted"
                                : job.is_approved
                                ? "Approved jobs cannot be deleted"
                                : "Delete job"
                            }
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        {totalCount > 0 && (
          <PaginationFooter
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            isLoading={tableLoading}
          />
        )}
      </GlassCard>

      {/* Log / Edit Job Modal */}
      <Modal
        isOpen={isModalOpen}
        size="lg"
        onClose={() => setIsModalOpen(false)}
        title={
          editingJob
            ? `Edit Job #${editingJob.sn || editingJob.id}`
            : "Log New Job Record"
        }
      >
        <form
          onSubmit={handleSubmit}
          className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4"
        >
          {formError && (
            <div className="sm:cht-col-span-2 cht-p-3.5 cht-bg-rose-50 cht-border cht-border-rose-200 cht-rounded-2xl cht-flex cht-items-center cht-gap-2.5 cht-text-rose-700 cht-text-xs cht-font-bold">
              <AlertCircle size={16} className="cht-flex-shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {/* Live Commission Calculator Box (Top of Modal) */}
          <div className="sm:cht-col-span-2 cht-p-3.5 cht-rounded-2xl cht-bg-blue-50/70 cht-border cht-border-blue-100 cht-flex cht-items-center cht-justify-between cht-gap-3">
            <div className="cht-flex cht-items-center cht-gap-2.5">
              <div className="cht-p-2 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da]">
                <Sparkles size={16} />
              </div>
              <div className="cht-text-xs">
                <span className="cht-text-[11px] cht-font-bold cht-text-gray-500 cht-uppercase cht-tracking-wider cht-block">
                  Calculated Worker Commission
                </span>
                <span className="cht-text-gray-400 cht-text-[11px]">
                  {formData.referral_source === "STAFF"
                    ? "Staff referral rate applied"
                    : formData.referral_source === "DIRECT"
                      ? "Direct client execution cut"
                      : "Walk-in base execution rate"}
                </span>
              </div>
            </div>

            <div className="cht-text-right">
              {previewCommission.valid ? (
                <span className="cht-text-[#3e57da] cht-font-black cht-text-lg">
                  ₦
                  {(previewCommission.amount || 0).toLocaleString("en-NG", {
                    minimumFractionDigits: 2,
                  })}
                </span>
              ) : (
                <span className="cht-text-rose-600 cht-font-bold cht-text-xs">
                  {previewCommission.error}
                </span>
              )}
            </div>
          </div>

          {/* Row 1: Date & Customer */}
          <Input
            label="Execution Date (Backdating Allowed)"
            type="date"
            required
            value={formData.date}
            onChange={(e: any) =>
              setFormData({ ...formData, date: e.target.value })
            }
          />

          <Input
            label="Customer / Client Name"
            type="text"
            required
            placeholder="e.g. John Okon, Acme Ventures"
            value={formData.customer_name}
            onChange={(e: any) =>
              setFormData({ ...formData, customer_name: e.target.value })
            }
          />

          {/* Row 2: Service Type & Referral Channel */}
          <Input
            as="select"
            label="Service Type"
            required
            value={formData.job_type}
            onChange={(e: any) =>
              setFormData({
                ...formData,
                job_type: e.target.value as ServiceType,
              })
            }
            options={dynamicServiceTypes.map((st) => ({
              label: st.label,
              value: st.value,
            }))}
          />

          <div>
            <label className="cht-block cht-text-xs cht-font-bold cht-text-[#1e293b] cht-mb-1.5 cht-px-0.5">
              Referral Channel
            </label>
            <div className="cht-grid cht-grid-cols-3 cht-gap-2">
              {[
                { id: "DIRECT", label: "Direct" },
                { id: "WALK_IN", label: "Walk-in" },
                { id: "STAFF", label: "Staff" },
              ].map((rf) => (
                <button
                  type="button"
                  key={rf.id}
                  onClick={() => {
                    const nextSource = rf.id as "WALK_IN" | "STAFF" | "DIRECT";
                    setFormData({
                      ...formData,
                      referral_source: nextSource,
                      amount_paid:
                        nextSource === "DIRECT"
                          ? "0"
                          : formData.amount_paid === "0"
                            ? ""
                            : formData.amount_paid,
                    });
                  }}
                  className={`cht-py-3 cht-px-2 cht-rounded-2xl cht-text-xs cht-font-bold cht-border cht-transition-all ${
                    formData.referral_source === rf.id
                      ? "cht-bg-[#3e57da] cht-text-white cht-border-[#3e57da] cht-shadow-sm"
                      : "cht-bg-gray-50 cht-text-gray-600 cht-border-slate-200 hover:cht-bg-gray-100"
                  }`}
                >
                  {rf.label}
                </button>
              ))}
            </div>

            {/* Helper Line for Selected Option */}
            <div className="cht-min-h-[22px] cht-mt-2 cht-px-0.5">
              <AnimatePresence mode="wait">
                <motion.p
                  key={formData.referral_source}
                  initial={{ opacity: 0, y: -2 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 2 }}
                  transition={{ duration: 0.15 }}
                  className="cht-text-[11px] cht-font-medium cht-text-gray-400 cht-flex cht-items-start cht-gap-1.5"
                >
                  <span className="cht-w-1.5 cht-h-1.5 cht-rounded-full cht-bg-[#3e57da] cht-mt-1 cht-flex-shrink-0" />
                  <span>
                    {formData.referral_source === "DIRECT" && (
                      <>
                        <span className="cht-font-bold cht-text-gray-700">
                          Direct:
                        </span>{" "}
                        Company client for your boss.
                      </>
                    )}
                    {formData.referral_source === "WALK_IN" && (
                      <>
                        <span className="cht-font-bold cht-text-gray-700">
                          Walk-in:
                        </span>{" "}
                        Office walk-in customer.
                      </>
                    )}
                    {formData.referral_source === "STAFF" && (
                      <>
                        <span className="cht-font-bold cht-text-gray-700">
                          Staff:
                        </span>{" "}
                        Referred by you.
                      </>
                    )}
                  </span>
                </motion.p>
              </AnimatePresence>
            </div>
          </div>

          {/* Row 3: Amount Paid (hidden on Direct) & Execution Status */}
          {formData.referral_source === "DIRECT" ? (
            <div className="cht-p-3.5 cht-rounded-2xl cht-bg-blue-50/60 cht-border cht-border-blue-100 cht-flex cht-items-center cht-gap-3">
              <Info
                size={18}
                className="cht-text-[#3e57da] cht-flex-shrink-0"
              />
              <div className="cht-text-xs cht-min-w-0">
                <span className="cht-font-bold cht-text-gray-800 cht-block">
                  Direct Company Client
                </span>
                <span className="cht-text-gray-500 cht-text-[11px]">
                  Price set to ₦0.00. Superadmin will update the price later.
                </span>
              </div>
            </div>
          ) : (
            <Input
              label="Amount Paid by Client"
              prefixText="₦"
              type="number"
              step="0.01"
              required
              placeholder="e.g. 11000.00"
              value={formData.amount_paid}
              onChange={(e: any) =>
                setFormData({ ...formData, amount_paid: e.target.value })
              }
              helperText={
                formData.referral_source === "STAFF" &&
                formData.job_type === "NERD"
                  ? "⭐ NERD Staff: Min ₦11k (₦1k commission + 100% excess)"
                  : undefined
              }
            />
          )}

          {/* Status */}
          <Input
            as="select"
            label="Execution Status"
            value={formData.status}
            onChange={(e: any) =>
              setFormData({ ...formData, status: e.target.value as JobStatus })
            }
            options={[
              { label: "Pending", value: "PENDING" },
              { label: "In Progress", value: "IN_PROGRESS" },
              { label: "Completed", value: "COMPLETED" },
              { label: "Cancelled", value: "CANCELLED" },
            ]}
          />

          {/* Remark (Full width) */}
          <div className="sm:cht-col-span-2">
            <Input
              as="textarea"
              multiline
              rows={2}
              label="Remarks / Notes (Optional)"
              placeholder="Notes, tracking numbers, or customer preferences..."
              value={formData.remark}
              onChange={(e: any) =>
                setFormData({ ...formData, remark: e.target.value })
              }
            />
          </div>

          {/* Actions */}
          <div className="sm:cht-col-span-2 cht-flex cht-items-center cht-justify-end cht-gap-3 cht-pt-4 cht-border-t cht-border-gray-100">
            <Button
              variant="outline"
              onClick={() => setIsModalOpen(false)}
              disabled={formSubmitting}
              className="cht-rounded-2xl"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formSubmitting || !previewCommission.valid}
              className="cht-rounded-2xl"
            >
              {formSubmitting
                ? "Saving..."
                : editingJob
                  ? "Update Job"
                  : "Log Job"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── MODAL: DELETE CONFIRMATION PROMPT ─────────────────────────────── */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!deleteSubmitting) {
            setDeleteModalOpen(false);
            setJobToDelete(null);
          }
        }}
        size="sm"
        icon={<Trash2 size={22} className="cht-text-rose-600" />}
        title="Delete Job Record"
        subtitle="Confirm job deletion"
        footer={
          <div className="cht-flex cht-items-center cht-justify-end cht-gap-2.5 cht-w-full">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setDeleteModalOpen(false);
                setJobToDelete(null);
              }}
              disabled={deleteSubmitting}
              className="cht-rounded-xl cht-text-xs"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleteSubmitting}
              className="cht-rounded-xl cht-text-xs cht-bg-rose-600 hover:cht-bg-rose-700 cht-text-white"
            >
              {deleteSubmitting ? "Deleting..." : "Yes, Delete Record"}
            </Button>
          </div>
        }
      >
        <div className="cht-py-2">
          <p className="cht-text-sm cht-text-gray-700 cht-font-medium">
            Are you sure you want to delete job record{" "}
            <span className="cht-font-black cht-text-gray-900">
              #{jobToDelete?.sn || jobToDelete?.id} ("{jobToDelete?.customer_name}")
            </span>?
          </p>
          <p className="cht-text-xs cht-text-gray-400 cht-mt-2">
            This action cannot be undone and will permanently remove this record from the system.
          </p>
        </div>
      </Modal>

      {/* ── MODAL: NOTIFICATION / STATUS PROMPT ───────────────────────────── */}
      <Modal
        isOpen={!!promptMessage}
        onClose={() => setPromptMessage(null)}
        size="sm"
        icon={<AlertCircle size={22} className={promptMessage?.type === "error" ? "cht-text-rose-600" : "cht-text-[#3e57da]"} />}
        title={promptMessage?.title || "Notification"}
        subtitle="Staff job notice"
        footer={
          <div className="cht-flex cht-justify-end cht-w-full">
            <Button
              variant="primary"
              type="button"
              onClick={() => setPromptMessage(null)}
              className="cht-rounded-xl cht-text-xs"
            >
              Dismiss
            </Button>
          </div>
        }
      >
        <p className="cht-text-sm cht-text-gray-700 cht-font-medium cht-py-2">
          {promptMessage?.message}
        </p>
      </Modal>

      {/* ── MODAL: KPI METRIC INFO ────────────────────────────────────────── */}
      <Modal
        isOpen={!!selectedMetricInfo}
        onClose={() => setSelectedMetricInfo(null)}
        size="md"
        icon={
          selectedMetricInfo === "TOTAL_JOBS" ? <Briefcase size={22} className="cht-text-blue-600" /> :
          selectedMetricInfo === "PENDING_JOBS" ? <Clock size={22} className="cht-text-amber-600" /> :
          selectedMetricInfo === "PAID_COMMISSIONS" ? <ShieldCheck size={22} className="cht-text-emerald-600" /> :
          selectedMetricInfo === "PENDING_PAYOUT" ? <Wallet size={22} className="cht-text-indigo-600" /> :
          selectedMetricInfo === "AWAITING_APPROVAL" ? <AlertCircle size={22} className="cht-text-purple-600" /> :
          undefined
        }
        title={
          selectedMetricInfo === "TOTAL_JOBS" ? "My Jobs Logged" :
          selectedMetricInfo === "PENDING_JOBS" ? "Total Pending Jobs" :
          selectedMetricInfo === "PAID_COMMISSIONS" ? "Paid Out (Disbursed)" :
          selectedMetricInfo === "PENDING_PAYOUT" ? "Pending Month Payout" :
          selectedMetricInfo === "AWAITING_APPROVAL" ? "Awaiting Approval" :
          ""
        }
        subtitle={
          selectedMetricInfo === "TOTAL_JOBS" ? "Personal task volume & client records" :
          selectedMetricInfo === "PENDING_JOBS" ? "Fulfillment queue & active backlog" :
          selectedMetricInfo === "PAID_COMMISSIONS" ? "Disbursed earnings in your account" :
          selectedMetricInfo === "PENDING_PAYOUT" ? "Approved earnings queued for monthly payroll" :
          selectedMetricInfo === "AWAITING_APPROVAL" ? "Completed tasks awaiting Superadmin verification" :
          ""
        }
        footer={
          <div className="cht-flex cht-justify-end cht-w-full">
            <Button
              variant="primary"
              type="button"
              onClick={() => setSelectedMetricInfo(null)}
              className="cht-rounded-xl cht-text-xs"
            >
              Close
            </Button>
          </div>
        }
      >
        <div className="cht-py-2 cht-space-y-4">
          {selectedMetricInfo === "TOTAL_JOBS" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-blue-50/70 cht-border cht-border-blue-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-blue-800 cht-uppercase cht-tracking-wide">Current View</span>
                  <div className="cht-text-xl cht-font-black cht-text-gray-900">
                    {stats.totalJobs} jobs logged
                  </div>
                </div>
                <div className="cht-text-right cht-text-xs cht-text-blue-900/80 cht-font-bold">
                  <div>Completed: <span className="cht-text-emerald-700">{stats.completed}</span></div>
                  <div>Pending: <span className="cht-text-amber-700">{stats.pending}</span></div>
                  {monthFilter !== "ALL" && (
                    <div className="cht-text-[10px] cht-text-blue-600 cht-font-medium cht-mt-0.5">
                      Month: {formatMonth(monthFilter)}
                    </div>
                  )}
                </div>
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> The total number of customer and agent jobs entered and managed under your staff account.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">Staff Best Practices</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li>Log client orders as soon as they are received to initiate fulfillment tracking.</li>
                    <li>Always specify the correct service type (NERD, Affidavit, CAC, TIN, etc.) so expected commission rates calculate accurately.</li>
                    <li>If the job was sourced for an external wholesale agent, link their agent profile to claim referral surplus margins.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "PENDING_JOBS" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-amber-50/70 cht-border cht-border-amber-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-amber-800 cht-uppercase cht-tracking-wide">Current Backlog</span>
                  <div className="cht-text-xl cht-font-black cht-text-amber-700">
                    {stats.pending} pending jobs
                  </div>
                </div>
                <div className="cht-text-right cht-text-xs cht-text-amber-900/80 cht-font-bold">
                  Active / In Progress
                  {monthFilter !== "ALL" && (
                    <div className="cht-text-[10px] cht-text-amber-700 cht-font-medium">
                      Month: {formatMonth(monthFilter)}
                    </div>
                  )}
                </div>
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> Jobs you have logged that are currently in{" "}
                  <span className="cht-font-bold cht-text-amber-700">PENDING</span> or{" "}
                  <span className="cht-font-bold cht-text-blue-700">IN_PROGRESS</span> status awaiting document completion or client handover.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">Fulfillment & Commission Rules</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li>Pending jobs do not release commissions until the work is finalized and marked as Completed.</li>
                    <li>Upload documents or deliver registration output promptly to keep turnaround time fast.</li>
                    <li>Once you mark a job as Completed, it moves into administrative review for Superadmin approval.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "PAID_COMMISSIONS" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-emerald-50/70 cht-border cht-border-emerald-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-emerald-800 cht-uppercase cht-tracking-wide">Total Disbursed</span>
                  <div className="cht-text-xl cht-font-black cht-text-emerald-700">
                    ₦{(stats.paidCommission || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {monthFilter !== "ALL" && (
                  <div className="cht-text-xs cht-font-bold cht-text-emerald-800">
                    {formatMonth(monthFilter)}
                  </div>
                )}
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> The total amount of worker commissions that have already been paid out to your bank account by management.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">Disbursement Details</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li>Includes both execution cuts (₦500 on NERD/Affidavit) and external agent referral margins.</li>
                    <li>These funds have cleared and been finalized in previous monthly payroll cycles.</li>
                    <li>Disbursed records are marked with green checkmarks in your jobs ledger.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "PENDING_PAYOUT" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-indigo-50/70 cht-border cht-border-indigo-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-indigo-800 cht-uppercase cht-tracking-wide">Ready for Payout</span>
                  <div className="cht-text-xl cht-font-black cht-text-indigo-700">
                    ₦{(stats.pendingMonthlyPayout || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {monthFilter !== "ALL" && (
                  <div className="cht-text-xs cht-font-bold cht-text-indigo-800">
                    {formatMonth(monthFilter)}
                  </div>
                )}
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> Approved commissions that are locked in and queued for transfer in the upcoming monthly payroll cycle.
                </p>
                <div className="cht-bg-indigo-900/5 cht-p-3.5 cht-rounded-xl cht-border cht-border-indigo-200/70 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-indigo-900 cht-uppercase cht-text-[10px] cht-tracking-wider">Monthly Cycle Policy</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-indigo-950">
                    <li>Commissions accumulate across each calendar month as jobs are completed and approved.</li>
                    <li>Management runs bulk monthly disbursements at month-end directly to your registered bank account.</li>
                    <li>Once disbursed, this balance automatically transitions to "Paid Out (Disbursed)".</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {selectedMetricInfo === "AWAITING_APPROVAL" && (
            <div className="cht-space-y-3.5">
              <div className="cht-p-3.5 cht-bg-purple-50/70 cht-border cht-border-purple-200/60 cht-rounded-2xl cht-flex cht-items-center cht-justify-between">
                <div>
                  <span className="cht-text-[10px] cht-font-black cht-text-purple-800 cht-uppercase cht-tracking-wide">Under Review</span>
                  <div className="cht-text-xl cht-font-black cht-text-purple-700">
                    ₦{(stats.awaitingApprovalCommission || 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                {monthFilter !== "ALL" && (
                  <div className="cht-text-xs cht-font-bold cht-text-purple-800">
                    {formatMonth(monthFilter)}
                  </div>
                )}
              </div>

              <div className="cht-text-xs cht-text-gray-600 cht-space-y-2 cht-leading-relaxed">
                <p>
                  <strong>What this represents:</strong> Commissions earned on jobs you have logged or completed that have not yet been approved by Superadmin.
                </p>
                <div className="cht-bg-gray-50 cht-p-3 cht-rounded-xl cht-border cht-border-gray-100 cht-space-y-1.5">
                  <p className="cht-font-black cht-text-gray-800 cht-uppercase cht-text-[10px] cht-tracking-wider">Approval Workflow</p>
                  <ul className="cht-list-disc cht-list-inside cht-space-y-1 cht-text-[11px] cht-text-gray-600">
                    <li>Superadmin reviews completed jobs to confirm customer payment and verify deliverables.</li>
                    <li>Upon Superadmin approval, this amount instantly moves into "Pending Month Payout".</li>
                    <li>If you have completed a job that is still awaiting approval, notify your supervisor for prompt clearance.</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Multi-Staff Task Handoff & Commission Split Modal */}
      <Modal
        isOpen={isHandoffModalOpen}
        onClose={() => {
          if (!handoffSubmitting) {
            setIsHandoffModalOpen(false);
            setHandoffTargetJob(null);
          }
        }}
        title="Deliverable Handoff & Commission Split"
      >
        {handoffTargetJob && (
          <form onSubmit={handleSubmitHandoff} className="cht-space-y-4">
            <div className="cht-p-3.5 cht-bg-blue-50/60 cht-border cht-border-blue-100 cht-rounded-2xl">
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-1">
                <span className="cht-text-[11px] cht-font-black cht-text-blue-700 cht-uppercase cht-tracking-wider">
                  Target Work Order
                </span>
                <span className="cht-text-xs cht-font-black cht-text-[#1e293b]">
                  #{handoffTargetJob.id}
                </span>
              </div>
              <p className="cht-text-sm cht-font-bold cht-text-gray-900">
                {handoffTargetJob.customer_name} • {handoffTargetJob.job_type_name || handoffTargetJob.job_type}
              </p>
              <p className="cht-text-xs cht-text-gray-500 cht-mt-0.5">
                Total Deliverable Commission:{" "}
                <strong className="cht-text-[#3e57da]">
                  ₦{(parseFloat(handoffTargetJob.commission || handoffTargetJob.expected_commission || "0") || 0).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                </strong>
              </p>
            </div>

            {handoffError && (
              <div className="cht-p-3 cht-bg-rose-50 cht-border cht-border-rose-200 cht-rounded-xl cht-text-xs cht-font-bold cht-text-rose-700">
                {handoffError}
              </div>
            )}

            {handoffSuccess && (
              <div className="cht-p-3 cht-bg-emerald-50 cht-border cht-border-emerald-200 cht-rounded-xl cht-text-xs cht-font-bold cht-text-emerald-700">
                {handoffSuccess}
              </div>
            )}

            {/* Successor Specialist Selection */}
            <div>
              <label className="cht-block cht-text-xs cht-font-black cht-text-gray-700 cht-mb-1.5 cht-uppercase cht-tracking-wider">
                Select Successor Specialist <span className="cht-text-rose-500">*</span>
              </label>
              <select
                value={handoffSuccessorId}
                onChange={(e) => setHandoffSuccessorId(e.target.value)}
                className="cht-w-full cht-px-3.5 cht-py-2.5 cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl cht-text-sm cht-font-medium cht-text-gray-800 focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-outline-none"
                required
              >
                <option value="">-- Choose Colleague Specialist --</option>
                {availableSpecialists.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.email || `Staff #${s.id}`})
                  </option>
                ))}
              </select>
            </div>

            {/* Split Presets & Percentage Configuration */}
            <div>
              <div className="cht-flex cht-items-center cht-justify-between cht-mb-1.5">
                <label className="cht-text-xs cht-font-black cht-text-gray-700 cht-uppercase cht-tracking-wider">
                  Commission Allocation Split
                </label>
                <div className="cht-flex cht-gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleSplitPreset(50)}
                    className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-text-[11px] cht-font-black cht-transition-colors ${
                      handoffPredecessorSplit === 50
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    50% / 50%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSplitPreset(70)}
                    className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-text-[11px] cht-font-black cht-transition-colors ${
                      handoffPredecessorSplit === 70
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    70% / 30%
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSplitPreset(30)}
                    className={`cht-px-2.5 cht-py-1 cht-rounded-lg cht-text-[11px] cht-font-black cht-transition-colors ${
                      handoffPredecessorSplit === 30
                        ? "cht-bg-[#3e57da] cht-text-white"
                        : "cht-bg-gray-100 cht-text-gray-600 hover:cht-bg-gray-200"
                    }`}
                  >
                    30% / 70%
                  </button>
                </div>
              </div>

              <div className="cht-grid cht-grid-cols-2 cht-gap-3">
                <div className="cht-p-3 cht-bg-indigo-50/50 cht-border cht-border-indigo-100 cht-rounded-xl">
                  <span className="cht-block cht-text-[10px] cht-font-black cht-text-indigo-800 cht-uppercase cht-tracking-wider cht-mb-1">
                    Your Share (Initial Handler)
                  </span>
                  <div className="cht-flex cht-items-center cht-gap-1">
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={handoffPredecessorSplit}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(99, parseInt(e.target.value || "0", 10)));
                        setHandoffPredecessorSplit(val);
                        setHandoffSuccessorSplit(100 - val);
                      }}
                      className="cht-w-16 cht-px-2 cht-py-1 cht-bg-white cht-border cht-border-indigo-200 cht-rounded-lg cht-text-sm cht-font-black cht-text-indigo-900 focus:cht-outline-none"
                    />
                    <span className="cht-text-sm cht-font-black cht-text-indigo-900">%</span>
                  </div>
                  <p className="cht-text-xs cht-font-black cht-text-indigo-700 cht-mt-1">
                    ≈ ₦{((parseFloat(handoffTargetJob.commission || handoffTargetJob.expected_commission || "0") || 0) * handoffPredecessorSplit / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </p>
                </div>

                <div className="cht-p-3 cht-bg-blue-50/50 cht-border cht-border-blue-100 cht-rounded-xl">
                  <span className="cht-block cht-text-[10px] cht-font-black cht-text-blue-800 cht-uppercase cht-tracking-wider cht-mb-1">
                    Successor Share (Completer)
                  </span>
                  <div className="cht-flex cht-items-center cht-gap-1">
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={handoffSuccessorSplit}
                      onChange={(e) => {
                        const val = Math.max(1, Math.min(99, parseInt(e.target.value || "0", 10)));
                        setHandoffSuccessorSplit(val);
                        setHandoffPredecessorSplit(100 - val);
                      }}
                      className="cht-w-16 cht-px-2 cht-py-1 cht-bg-white cht-border cht-border-blue-200 cht-rounded-lg cht-text-sm cht-font-black cht-text-blue-900 focus:cht-outline-none"
                    />
                    <span className="cht-text-sm cht-font-black cht-text-blue-900">%</span>
                  </div>
                  <p className="cht-text-xs cht-font-black cht-text-blue-700 cht-mt-1">
                    ≈ ₦{((parseFloat(handoffTargetJob.commission || handoffTargetJob.expected_commission || "0") || 0) * handoffSuccessorSplit / 100).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
            </div>

            {/* Handover Reason / Work Accomplished */}
            <div>
              <label className="cht-block cht-text-xs cht-font-black cht-text-gray-700 cht-mb-1.5 cht-uppercase cht-tracking-wider">
                Handover Notes / Reason
              </label>
              <textarea
                value={handoffReason}
                onChange={(e) => setHandoffReason(e.target.value)}
                placeholder="Describe work completed so far, pending steps, or reason for transfer..."
                className="cht-w-full cht-px-3.5 cht-py-2.5 cht-bg-gray-50 cht-border cht-border-gray-200 cht-rounded-xl cht-text-xs cht-text-gray-800 focus:cht-bg-white focus:cht-border-[#3e57da] focus:cht-outline-none cht-min-h-[70px]"
              />
            </div>

            <div className="cht-pt-2 cht-flex cht-items-center cht-justify-end cht-gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsHandoffModalOpen(false)}
                disabled={handoffSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={handoffSubmitting}
                className="cht-bg-[#3e57da] hover:cht-bg-[#3247b5] cht-text-white"
              >
                {handoffSubmitting ? "Executing Handoff..." : "Confirm Handoff & Split"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default AdminStaffJobs;
